<script setup lang="ts">
import { computed } from 'vue';
import UiText from './UiText.vue';
import UiButton from './UiButton/index';
import UiNavLink from './UiNavLink.vue';
import UiTag from './UiTag.vue';
import UiTooltip from './UiTooltip.vue';
import { UiCardPaddingClasses, UiCardRadiusClasses } from './shared/constants';

const paddingClasses = computed(() => {
    return UiCardPaddingClasses[props.padding];
});

const radiusClasses = computed(() => {
    return UiCardRadiusClasses[props.radius];
});

interface UiCardProps {
    heading?: string;
    text?: string;
    buttonLabel?: string;
    buttonHref?: string;
    href?: string;
    linkMode?: 'wrap' | 'overlay';
    tag?: string;
    tagVariant?: 'primary' | 'secondary' | 'rounded';
    hover?: boolean | 'dark' | 'border' | 'shadow';
    padding?: 'sm' | 'md' | 'lg';
    radius?: 'sm' | 'md';
    /** Show a mouse-tracking pill with custom text */
    trackingPill?: string;
}

const props = withDefaults(defineProps<UiCardProps>(), {
    hover: false,
    linkMode: 'wrap',
    padding: 'sm',
    radius: 'md'
});
const isLink = computed(() => Boolean(props.href));
const shouldWrapLink = computed(() => isLink.value && props.linkMode === 'wrap');
const shouldOverlayLink = computed(() => isLink.value && props.linkMode === 'overlay');
const enableDarkHover = computed(() => (props.hover === 'dark'));
const enableBorderHover = computed(() => (props.hover === 'border'));
const enableShadowHover = computed(() => (props.hover === 'shadow'));
const overlayAriaLabel = computed(() => props.heading ? `Open ${props.heading}` : 'Open');
</script>

<template>
    <UiTooltip 
        v-if="props.trackingPill" 
        :content="props.trackingPill" 
        :follow-cursor="true"
    >
        <div
            class="border border-se-gray-300 overflow-hidden bg-se-white se-transition-all"
            :class="[
                shouldOverlayLink ? 'relative' : '',
                radiusClasses,
                enableDarkHover ? 'group/buttons-container' : enableBorderHover ? 'group/buttons-container-border' : '',
                enableBorderHover ? 'hover:border-se-dark' : '',
                enableShadowHover ? 'hover:shadow-lg hover:border-se-dark cursor-pointer' : '',
            ]"
        >
            <component :is="shouldWrapLink ? UiNavLink : 'div'" :to="shouldWrapLink ? props.href : undefined" class="h-full block">
                <slot name="preview" />
                <div
                    class="p-4 se-transition-all h-full w-full"
                    :class="[
                        paddingClasses,
                        enableDarkHover ? 'group-hover/buttons-container:bg-se-dark group-hover/buttons-container:text-se-white' : enableBorderHover ? 'group-hover/buttons-container-border:border-se-dark' : '']"
                >
                    <slot name="content">
                        <div v-if="props.tag" class="mb-3">
                            <UiTag :text="props.tag" :variant="props.tagVariant || 'rounded'" />
                        </div>
                        <UiText as="h3" variant="h4" :text="props.heading" class="font-semibold mb-4" v-if="props.heading" />
                        <UiText as="p" variant="p" :text="props.text" v-if="props.text" />
                        <slot />
                        <div v-if="props.buttonLabel" class="mt-4">
                            <UiButton :href="props.buttonHref" :label="props.buttonLabel" variant="ghost" hover-style="vibrant" :show-arrow="true" justify="start" inline />
                        </div>
                    </slot>
                </div>
            </component>
            <UiNavLink
                v-if="shouldOverlayLink"
                :to="props.href"
                class="absolute inset-0 z-10"
                :aria-label="overlayAriaLabel"
            />
        </div>
    </UiTooltip>
    
    <div
        v-else
        class="border border-se-gray-300 overflow-hidden bg-se-white se-transition-all"
        :class="[
            shouldOverlayLink ? 'relative' : '',
            radiusClasses,
            enableDarkHover ? 'group/buttons-container' : enableBorderHover ? 'group/buttons-container-border' : '',
            enableBorderHover ? 'hover:border-se-dark' : '',
            enableShadowHover ? 'hover:shadow-lg hover:border-se-dark cursor-pointer' : '',
        ]"
    >
        <component :is="shouldWrapLink ? UiNavLink : 'div'" :to="shouldWrapLink ? props.href : undefined" class="h-full block">
            <slot name="preview" />
            <div
                class="p-4 se-transition-all h-full w-full"
                :class="[
                    paddingClasses,
                    enableDarkHover ? 'group-hover/buttons-container:bg-se-dark group-hover/buttons-container:text-se-white' : enableBorderHover ? 'group-hover/buttons-container-border:border-se-dark' : '']"
            >
                <slot name="content">
                    <div v-if="props.tag" class="mb-3">
                        <UiTag :text="props.tag" :variant="props.tagVariant || 'rounded'" />
                    </div>
                    <UiText as="h3" variant="h4" :text="props.heading" class="font-semibold mb-4" v-if="props.heading" />
                    <UiText as="p" variant="p" :text="props.text" v-if="props.text" />
                    <slot />
                    <div v-if="props.buttonLabel" class="mt-4">
                        <UiButton :href="props.buttonHref" :label="props.buttonLabel" variant="ghost" hover-style="vibrant" :show-arrow="true" justify="start" inline />
                    </div>
                </slot>
            </div>
        </component>
        <UiNavLink
            v-if="shouldOverlayLink"
            :to="props.href"
            class="absolute inset-0 z-10"
            :aria-label="overlayAriaLabel"
        />
    </div>
</template>

